import React, { useState, useEffect } from 'react';
import {
  Box, Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import StockBalanceAutocomplete from '../../components/StockBalanceAutocomplete';
import { createStockAdjustment } from '../../api/stockAdjustment';
import { getStores } from '../../api/stockLedger';
import useActiveStore from '../../hooks/useActiveStore';
import { filterStoresByAssignment } from '../../utils/assignedStores';
import { toInventoryList } from '../../api/inventory-request';
import { STOCK_ADJUSTMENT_TYPES } from '../../constants/storeManagement';
import { storeDisplayName } from '../../utils/storeEntity';
import { IPD_COLORS } from '../../theme';

const LINE_DEFAULT = {
  item_key: '',
  item_id: '',
  item_name: '',
  batch_number: '',
  grn_item_id: '',
  expiry_date: '',
  uom_id: '',
  adjustment_type: 'INCREASE',
  adjustment_quantity: '',
  available: '',
  remarks: '',
  _batches: [],
};

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function StockAdjustmentForm() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const listUrl = url.replace(/\/adjustment\/new$/, '/adjustments');

  const [stores, setStores] = useState([]);
  const [lines, setLines] = useState([{ ...LINE_DEFAULT }]);
  const [saving, setSaving] = useState(false);

  const { storeLocked, activeStoreId, assignedStoreIds } = useActiveStore();

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      store_id: '',
      reason: '',
      remarks: '',
    },
  });

  const storeId = watch('store_id');

  useEffect(() => {
    getStores()
      .then(d => setStores(toInventoryList(d)))
      .catch(() => {});
  }, []);

  // Store-scoped users work in one store only — pre-fill and lock it. Guarded on
  // membership: setting a value that is not in `options` renders the Select blank.
  const lockStore = storeLocked && stores.some(s => (s.store_id || s.id) === activeStoreId);

  useEffect(() => {
    if (lockStore) setValue('store_id', activeStoreId);
  }, [lockStore, activeStoreId, setValue]);


  function updateLine(i, patch) {
    setLines(prev => prev.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }

  function selectItem(i, key, item) {
    if (!item) {
      updateLine(i, { ...LINE_DEFAULT });
      return;
    }
    const batches = item.batches || [];
    updateLine(i, {
      item_key: key || item.item_id,
      item_id: item.item_id,
      item_name: item.item_name || item.item_code || '',
      uom_id: item.base_uom_id || '',
      batch_number: '',
      grn_item_id: '',
      expiry_date: '',
      available: item.total_available_quantity ?? '',
      _batches: batches,
    });
  }

  function selectBatch(i, batchNumber) {
    const line = lines[i];
    const batch = (line._batches || []).find(b => b.batch_number === batchNumber);
    updateLine(i, {
      batch_number: batchNumber,
      grn_item_id: batch?.grn_item_id || '',
      expiry_date: batch?.expiry_date || line.expiry_date || '',
      available: batch?.available_quantity ?? line.available,
    });
  }

  async function onSubmit(data) {
    const valid = lines.filter(l => l.item_id && toNumber(l.adjustment_quantity) > 0 && l.adjustment_type);
    if (!valid.length) {
      toast.error('Add at least one line with item and quantity');
      return;
    }
    if (!data.store_id) {
      toast.error('Store is required');
      return;
    }

    const overAvailable = valid.find(l => {
      if (l.adjustment_type !== 'DECREASE') return false;
      const qty = toNumber(l.adjustment_quantity);
      const avail = toNumber(l.available);
      return avail != null && qty != null && qty > avail;
    });
    if (overAvailable) {
      toast.error(
        `Decrease qty cannot exceed available (${overAvailable.available}) for ${overAvailable.item_name || 'item'}`,
      );
      return;
    }

    setSaving(true);
    try {
      // API: POST /inventory/stock-adjustments/
      const created = await createStockAdjustment({
        store_id: data.store_id,
        reason: data.reason || undefined,
        remarks: data.remarks || undefined,
        lines: valid.map(l => ({
          item_id: l.item_id,
          adjustment_type: l.adjustment_type,
          adjustment_quantity: Number(l.adjustment_quantity),
          batch_number: l.batch_number || undefined,
          grn_item_id: l.grn_item_id || undefined,
          expiry_date: l.expiry_date || undefined,
          uom_id: l.uom_id || undefined,
          remarks: l.remarks || undefined,
        })),
      });
      toast.success('Draft adjustment created');
      const id = created?.id;
      history.push(id ? `${listUrl}/${id}` : listUrl);
    } catch (e) {
      toast.error(e.message || 'Failed to create adjustment');
    } finally {
      setSaving(false);
    }
  }

  const storeOptions = filterStoresByAssignment(stores, assignedStoreIds).map(s => ({
    value: s.store_id || s.id,
    label: storeDisplayName(s),
  }));

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <PageHeader
        eyebrow="Inventory Management · Stock Adjustments"
        title="New Adjustment"
        description="Creates a DRAFT — stock changes only after authorize"
        actions={(
          <>
            <InvButton variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push(listUrl)}>
              Back
            </InvButton>
            <InvButton type="submit" size="small" loading={saving}>
              Create draft
            </InvButton>
          </>
        )}
      />

      <SectionCard title="Adjustment details">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="store_id"
              control={control}
              rules={{ required: 'Required' }}
              render={({ field }) => (
                <InvSelect
                  label="Store *"
                  value={field.value}
                  onChange={field.onChange}
                  options={storeOptions}
                  error={errors.store_id}
                  disabled={lockStore}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="reason"
              control={control}
              render={({ field }) => <InvTextField {...field} label="Reason" />}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="remarks"
              control={control}
              render={({ field }) => <InvTextField {...field} label="Remarks" multiline rows={2} />}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Lines" sx={{ mt: 2.5 }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 240 }}>Item *</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 140 }}>Batch</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 120 }}>Type *</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 110 }}>Qty *</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>Avail</TableCell>
                <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 130 }}>Expiry</TableCell>
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <StockBalanceAutocomplete
                        storeId={storeId}
                        value={line.item_key}
                        onChange={(key, item) => selectItem(i, key, item)}
                        placeholder="Search item…"
                        disabled={!storeId}
                      />
                    </TableCell>
                    <TableCell>
                      <InvSelect
                        label=""
                        value={line.batch_number}
                        onChange={e => selectBatch(i, e.target.value)}
                        options={[
                          { value: '', label: '—' },
                          ...(line._batches || []).map(b => ({
                            value: b.batch_number || '',
                            label: `${b.batch_number || '—'} · ${b.available_quantity ?? '—'}`,
                          })).filter(o => o.value),
                        ]}
                        disabled={!line.item_id}
                      />
                    </TableCell>
                    <TableCell>
                      <InvSelect
                        label=""
                        value={line.adjustment_type}
                        onChange={e => updateLine(i, { adjustment_type: e.target.value })}
                        options={STOCK_ADJUSTMENT_TYPES}
                      />
                    </TableCell>
                    <TableCell>
                      <InvTextField
                        value={line.adjustment_quantity}
                        label=""
                        type="number"
                        inputProps={{
                          min: 0.01,
                          step: 0.01,
                          ...(line.adjustment_type === 'DECREASE' && toNumber(line.available) != null
                            ? { max: toNumber(line.available) }
                            : {}),
                        }}
                        error={
                          line.adjustment_type === 'DECREASE'
                          && toNumber(line.adjustment_quantity) != null
                          && toNumber(line.available) != null
                          && toNumber(line.adjustment_quantity) > toNumber(line.available)
                        }
                        helperText={
                          line.adjustment_type === 'DECREASE'
                          && toNumber(line.adjustment_quantity) != null
                          && toNumber(line.available) != null
                          && toNumber(line.adjustment_quantity) > toNumber(line.available)
                            ? `Max ${line.available}`
                            : undefined
                        }
                        onChange={e => updateLine(i, { adjustment_quantity: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{line.available || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <InvTextField
                        value={line.expiry_date || ''}
                        label=""
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        onChange={e => updateLine(i, { expiry_date: e.target.value })}
                        disabled={line.adjustment_type === 'DECREASE'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        <IconButton
                          size="small"
                          aria-label="Delete row"
                          disabled={lines.length <= 1}
                          onClick={() => setLines(prev => prev.filter((_, idx) => idx !== i))}
                          sx={{ color: IPD_COLORS.error.text }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <Tooltip title="Add row">
                          <IconButton
                            size="small"
                            aria-label="Add row below"
                            onClick={() => setLines(prev => [
                              ...prev.slice(0, i + 1),
                              { ...LINE_DEFAULT },
                              ...prev.slice(i + 1),
                            ])}
                            sx={{ color: IPD_COLORS.primary }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Box>
      </SectionCard>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
        <InvButton variant="outlined" onClick={() => history.push(listUrl)}>Cancel</InvButton>
        <InvButton type="submit" loading={saving}>Create draft</InvButton>
      </Box>
    </Box>
  );
}
